import { newsFetchMessage } from './NewsCell.recencyHelpers';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Divider,
    IconButton,
    Link,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import { alpha } from '@mui/material/styles';
import { watchlistPopoverPaperSx } from '../../styles/watchlistMsiStyles';
import MovePill from './MovePill';
import { selectRecentArticles, DEFAULT_RECENCY_WINDOW_HOURS } from './NewsCell.recencyHelpers';

/**
 * Formats "Updated N min ago" copy for the popover header.
 * Falls back to an empty string when we don't have a valid Date yet.
 */
function formatUpdatedAgo(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '';
    }
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return 'Updated just now';
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return 'Updated just now';
    if (minutes === 1) return 'Updated 1 min ago';
    if (minutes < 60) return `Updated ${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return 'Updated 1 hr ago';
    if (hours < 24) return `Updated ${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `Updated ${days}d ago`;
}

function formatRelativePublished(publishedAt) {
    if (!publishedAt) return '';
    const parsed = publishedAt instanceof Date ? publishedAt : new Date(publishedAt);
    if (Number.isNaN(parsed.getTime())) return '';
    const diffMs = Date.now() - parsed.getTime();
    const minutes = Math.max(0, Math.round(diffMs / 60000));
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function normalizeUrl(raw) {
    if (raw == null) return '';
    return String(raw).trim();
}

/**
 * Popover surface: header row (title + "Updated N min ago" + move pill),
 * headline list, and a Deep Dive button slot.
 *
 * W7.1 (Trevor 2026-08-01) changes:
 *   - Mover chip → shared `MovePill` (same solid-color pill as the cell,
 *     white text on the color band; tooltip "Current price versus previous
 *     day close").
 *   - Refresh icon-button removed from the header (polling + tab-focus
 *     refetch in `useWatchlistNews` still runs; the manual affordance was
 *     redundant per Trevor: "users would keep clicking that button mostly
 *     pointlessly"). Everything else in the popover layout — the "Updated
 *     N min ago" header text, the article-list rendering, the OQ-5 Yahoo
 *     icon-button, the Deep Dive slot — stays as-is.
 *
 * Per OQ-5 (Trevor 2026-07-27) the canonical `url` is the primary click
 * target on the headline title. When `url !== yahoo_url`, we additionally
 * render a small "Open on Yahoo Finance" icon-button next to the title.
 * When they match, no icon is rendered.
 *
 * W8 (2026-08-04): on-open the popover fires `onArticlesRead(articleIds)`
 * for every article inside the recency window (`recencyWindowHours`) that
 * isn't already marked read. Read-marking happens only on open — never on
 * hover / scroll / other implicit gestures — per Trevor's 2026-07-30 spec.
 * Batching is upstream in `useWatchlistNews.markArticleRead`, which coalesces
 * the N per-open calls into a single Firestore batch commit.
 *
 * Deep Dive remains behind the parent-controlled feature flag. When enabled,
 * clicking it fires `onDeepDiveRequest()` — the parent (`NewsCell`) owns the
 * settings + deep-dive dialog state. When `is_big_mover`, the button is
 * styled `contained` (emphasized) rather than `outlined` (subtler), keeping
 * the button a single style tier across the two cases.
 */
export default function NewsPopover({
    anchorEl,
    open,
    onClose,
    item,
    // Two-stage news loading (Fix 2, 2026-08-12): `articles` no longer comes
    // out of `item.articles` — the metadata endpoint doesn't ship bodies. The
    // parent (`NewsCell`) passes detail bodies here once the on-demand fetch
    // resolves; before that it passes the metadata's `recent_articles` stubs
    // (id + published_at) so mark-read can fire immediately on open even
    // though titles aren't rendered yet. `articlesLoading` gates the loading
    // skeleton so we don't render a wall of "Untitled" while detail is in
    // flight.
    articles: articlesProp = null,
    articlesLoading = false,
    articlesError = null,
    moverThresholdPct,
    lastUpdatedAt,
    deepDiveEnabled = false,
    recencyWindowHours = DEFAULT_RECENCY_WINDOW_HOURS,
    readArticleIds = null,
    onArticlesRead = null,
    onDeepDiveRequest = null,
}) {
    const articles = Array.isArray(articlesProp) ? articlesProp : [];
    // "Detail-loaded" iff any article carries a title. During the on-demand
    // fetch the parent still hands us stubs (article_id + published_at only),
    // which lets mark-read fire but shouldn't render as headlines.
    const hasDetailBodies = articles.some((a) => Boolean(a?.title));
    const rawReturnPct = item?.return_pct;
    const returnPct = Number(rawReturnPct);
    const hasReturnPct = rawReturnPct !== null
        && rawReturnPct !== undefined
        && Number.isFinite(returnPct);
    const displayName = String(item?.display_name || '').trim() || 'News';

    // Fire mark-read for every visible article that (a) has a real
    // article_id, (b) is inside the recency window, and (c) is not already
    // marked read locally. Only fires when the popover transitions from
    // closed → open; the `openRef` guard means re-renders while the popover
    // stays open don't re-fire the write burst.
    const openRef = useRef(false);
    useEffect(() => {
        if (!open) {
            openRef.current = false;
            return;
        }
        if (openRef.current) return;
        openRef.current = true;
        if (typeof onArticlesRead !== 'function') return;
        const recent = selectRecentArticles(articles, recencyWindowHours);
        if (recent.length === 0) return;
        const readSet = readArticleIds instanceof Set ? readArticleIds : null;
        const toMark = [];
        recent.forEach((article) => {
            const rawId = article?.article_id;
            if (rawId === null || rawId === undefined) return;
            const articleId = String(rawId).trim();
            if (!articleId) return;
            if (readSet && readSet.has(articleId)) return;
            toMark.push(articleId);
        });
        if (toMark.length > 0) {
            onArticlesRead(toMark);
        }
    }, [open, articles, recencyWindowHours, readArticleIds, onArticlesRead]);

    return (
        <Popover
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
                sx: [
                    {
                        width: 380,
                        maxHeight: 460,
                        mt: 0.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: (theme) => alpha(
                            theme.palette.divider,
                            theme.palette.mode === 'dark' ? 0.34 : 0.62,
                        ),
                        boxShadow: (theme) => (theme.palette.mode === 'dark'
                            ? '0 18px 32px rgba(0, 0, 0, 0.42)'
                            : '0 16px 32px rgba(15, 23, 42, 0.14)'),
                    },
                    watchlistPopoverPaperSx,
                ],
            }}
        >
            <Box display="flex" alignItems="center" gap={1} sx={{ px: 1.5, py: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                        title={displayName}
                    >
                        {displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {formatUpdatedAgo(lastUpdatedAt)}
                    </Typography>
                </Box>
                {hasReturnPct && (
                    <MovePill
                        pct={returnPct}
                        threshold={Number.isFinite(Number(moverThresholdPct))
                            ? Number(moverThresholdPct)
                            : 2.0}
                        testId="news-mover-chip"
                    />
                )}
            </Box>
            <Divider />
            <Box sx={{ px: 1.5, py: 1, maxHeight: 300, overflowY: 'auto' }}>
                {articlesLoading && !hasDetailBodies ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', textAlign: 'center', py: 2.5 }}
                        data-testid="news-popover-loading"
                    >
                        Loading headlines…
                    </Typography>
                ) : articlesError && !hasDetailBodies ? (
                    <Typography
                        variant="body2"
                        color="error"
                        sx={{ fontSize: '0.75rem', textAlign: 'center', py: 2.5 }}
                        data-testid="news-popover-error"
                    >
                        Couldn't load headlines. Try reopening in a moment.
                    </Typography>
                ) : !hasDetailBodies ? (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', textAlign: 'center', py: 2.5 }}
                        data-testid="news-popover-empty"
                    >
                        {newsFetchMessage(item)}
                    </Typography>
                ) : (
                    articles.map((article, idx) => {
                        const canonicalUrl = normalizeUrl(article?.url);
                        const yahooUrl = normalizeUrl(article?.yahoo_url);
                        const showYahooIcon = Boolean(canonicalUrl)
                            && Boolean(yahooUrl)
                            && canonicalUrl !== yahooUrl;
                        const primaryUrl = canonicalUrl || yahooUrl;
                        const key = `${primaryUrl || 'article'}-${idx}`;
                        const summary = String(article?.summary || '').trim();
                        return (
                            <Box
                                key={key}
                                sx={{
                                    py: 0.75,
                                    ...(idx > 0 ? {
                                        borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                    } : {}),
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Tooltip
                                        title={summary}
                                        arrow
                                        disableHoverListener={!summary}
                                    >
                                        <Link
                                            href={primaryUrl || undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                            onClick={(event) => event.stopPropagation()}
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: '0.78rem',
                                                fontWeight: 600,
                                                lineHeight: 1.35,
                                                color: 'text.primary',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {article?.title || 'Untitled'}
                                        </Link>
                                    </Tooltip>
                                    {showYahooIcon && (
                                        <Tooltip title="Open on Yahoo Finance" arrow>
                                            <IconButton
                                                size="small"
                                                component="a"
                                                href={yahooUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label="Open on Yahoo Finance"
                                                onClick={(event) => event.stopPropagation()}
                                                sx={{ p: 0.25, color: 'text.secondary' }}
                                                data-testid="news-yahoo-icon"
                                            >
                                                <LaunchIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.75, mt: 0.25, alignItems: 'center' }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: '0.65rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '55%',
                                        }}
                                    >
                                        {article?.publisher || 'Unknown'}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                                        {`• ${formatRelativePublished(article?.published_at)}`}
                                    </Typography>
                                </Box>
                                {summary && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            fontSize: '0.68rem',
                                            mt: 0.25,
                                        }}
                                    >
                                        {summary}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })
                )}
            </Box>
            <Divider />
            <Box sx={{ px: 1.5, py: 1 }}>
                {deepDiveEnabled ? (
                    <Button
                        size="small"
                        variant={item?.is_big_mover ? 'contained' : 'outlined'}
                        fullWidth
                        aria-label="Deep Dive"
                        data-testid="news-deep-dive-button"
                        onClick={() => {
                            if (typeof onDeepDiveRequest === 'function') {
                                onDeepDiveRequest();
                            }
                        }}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.72rem',
                            fontWeight: 600,
                        }}
                    >
                        Deep Dive
                    </Button>
                ) : (
                    <Tooltip
                        title="Deep Dive is not yet available."
                        arrow
                    >
                        <span>
                            <Button
                                size="small"
                                variant="outlined"
                                fullWidth
                                disabled
                                aria-label="Deep Dive"
                                data-testid="news-deep-dive-button"
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                }}
                            >
                                Deep Dive
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Box>
        </Popover>
    );
}

NewsPopover.propTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    anchorEl: PropTypes.any,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    item: PropTypes.shape({
        display_name: PropTypes.string,
        is_big_mover: PropTypes.bool,
        return_pct: PropTypes.number,
        recent_articles: PropTypes.array,
    }),
    articles: PropTypes.array,
    articlesLoading: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    articlesError: PropTypes.any,
    moverThresholdPct: PropTypes.number,
    lastUpdatedAt: PropTypes.instanceOf(Date),
    deepDiveEnabled: PropTypes.bool,
    recencyWindowHours: PropTypes.number,
    readArticleIds: PropTypes.instanceOf(Set),
    onArticlesRead: PropTypes.func,
    onDeepDiveRequest: PropTypes.func,
};
